import { Competition, CompetitionAttributes } from './Competition';
import { ListProvider } from './ListProvider';
import { PublicUser, User } from './User';
import { Resolver } from './Resolver';
import { EventRegistration } from './EventRegistration';
import { EventAdminInvitation } from './Events/EventAdminInvitation';
import { Team } from './Team';
import { TeamCategory, TeamColor, TeamDivision } from './TeamAttributes';
import { RevSportIntegration } from './RevSportIntegration';
import { EventVenue } from './Venue';

export interface HeroImage {
    url?: string;
    dataProviderReferenceString ?: string;
}

export interface UpdatableEventAttributes {
    name: string;
    competitionDateRange: DateRange;
    heroImage?: HeroImage;
    color?: TeamColor
}

export interface EventAttributes extends UpdatableEventAttributes {
    category?: TeamCategory;
    division?: TeamDivision;

    displayCompetitionDates: boolean;
}

export interface DateRange {
    startDate: Date;
    endDate: Date;
}

export interface Event {
    id: string;
    attributes: EventAttributes;

    competitionProvider: ListProvider<Resolver<Competition>>;
    enteredTeams: ListProvider<Resolver<Team>>;
    venues: ListProvider<Resolver<EventVenue>>;
    articles: ListProvider<Resolver<EventArticle>>;
    postedNotifications: ListProvider<Resolver<EventNotification>>;
    scheduledNotifications: ListProvider<Resolver<EventNotification>>;
    informationItems: ListProvider<Resolver<EventInformationItem>>;

    registration?: EventRegistration;

    redeemAdminInvitation: (token: string, user: User) => Promise<void>;
    adminForUser: (user: User) => Promise<EventAdmin>;
}

export function isEvent(candidate): candidate is Event {
    return (candidate as Event).attributes !== undefined;
}

export function isSameDay(left: Date, right: Date) {
    return (
        left.getDate() === right.getDate() &&
        left.getMonth() === right.getMonth() &&
        left.getFullYear() === right.getFullYear()
    );
}

export function sortEvents(left: Event, right: Event) {
    const leftHasDates = left.attributes.displayCompetitionDates;
    const rightHasDates = right.attributes.displayCompetitionDates;
    if (leftHasDates) {
        if (rightHasDates) {
            const leftStartDate = left.attributes.competitionDateRange.startDate;
            const rightStartDate = right.attributes.competitionDateRange.startDate;
            if (isSameDay(leftStartDate, rightStartDate)) {
                const leftEndDate = left.attributes.competitionDateRange.endDate;
                const rightEndDate = right.attributes.competitionDateRange.endDate;
                return leftEndDate > rightEndDate ? -1 : 1;
            } else {
                return leftStartDate > rightStartDate ? -1 : 1;
            }
        } else {
            return -1;
        }
    } else if (rightHasDates) {
        return 1;
    }
    if (left.attributes.name < right.attributes.name) {
        return -1;
    } else if (left.attributes.name > right.attributes.name) {
        return 1;
    } else {
        return 0;
    }
}

export enum ManageCompetitionError {
    unauthenticated,

    missingParameters,
    missingPermission,
    notFound,
    serverFailure,
}

export enum EventAdminError {
    unauthenticated,
    missingPermission,

    missingRequiredAttributes,
}

export enum EventRegistrationError {
    unauthenticated,
    missingPermission,

    serverFailure,
}

export enum EventExternalMatchSource {
    refTown,
    revSport,
    none,
}

export interface EventArticle {
    id: string;
    publishDate: Date;

    thumbnailUrl: string;
    title: string;
    url: string;
}

export interface EventNotification {
    id: string;
    publishDate: Date;
    message: string;
}

export interface EventInformationItem {
    id: string;
    createdDate: Date;
    text: string;
    content: EventInformationContent;
    order?: number;
}

export interface EventInformationContent {}

export interface EventInformationLinkContent {
    url: string;
}

export interface EventInformationFolderContent {
    items: string[];  // Array of item IDs in this folder
}

export enum EventAdminPermission {
    canEditEvent = 'editEvent',
    canEditCompetitions = 'editCompetitions',
    canEditTeams = 'editTeams',
    canEditMatches = 'editMatches',
    canEditStats = 'editStats',
}


export interface VenueName {
    name: string;
    subvenueName?: string;
}

export interface EventAdmin {
    user: User;
    event: Event;
    externalMatchSource: EventExternalMatchSource;

    update: (attributes: UpdatableEventAttributes) => Promise<Event>;

    addArticle: (thumbnailUrl: string, title: string, url: string) => Promise<EventArticle>;
    removeArticle: (article: EventArticle) => Promise<void>;

    addVenue: (name: VenueName, timezone: string, externalID?: string) => Promise<EventVenue>;
    updateVenue: (venue: EventVenue, name: VenueName, timezone: string) => Promise<EventVenue>;
    removeVenue: (venue: EventVenue) => Promise<void>;

    postNotification: (message: string) => Promise<void>;

    addScheduledNotification: (message: string, publishDate: Date) => Promise<void>;
    postScheduledNotification: (id: string) => Promise<void>;
    removeScheduledNotification: (id: string) => Promise<void>;

    addInformationItem: (text: string, content: EventInformationContent) => Promise<EventInformationItem>;
    updateInformationItem: (item: EventInformationItem, text: string, content: EventInformationContent) => Promise<EventInformationItem>;
    removeInformationItem: (item: EventInformationItem) => Promise<void>;

    eventEditor?: EventEditor;
    competitionEditor?: EventCompetitionEditor;
    revSportIntegration?: RevSportIntegration;

    openRegistration: (maxNumberOfEntries: number, price?: number) => Promise<EventRegistration>;
    closeRegistration: () => Promise<void>;

    delete: () => Promise<void>;
}

export interface EventCompetitionEditor {
    addCompetition: (attributes: CompetitionAttributes) => Promise<Competition>;
    updateCompetition: (
        competition: Competition,
        attributes: CompetitionAttributes
    ) => Promise<Competition>;
    removeCompetition: (competition: Competition) => Promise<void>;
}

export interface EventEditor {
    adminProvider: ListProvider<Resolver<{ user: PublicUser; permission: EventAdminPermission }>>;
    adminInvitationProvider: ListProvider<Resolver<EventAdminInvitation>>;

    updateAdminPermission: (user: PublicUser, permission: EventAdminPermission) => Promise<void>;
    removeAdmin: (user: PublicUser) => Promise<void>;

    sendAdminInvitation: (
        email: string,
        permission: EventAdminPermission
    ) => Promise<EventAdminInvitation>;
    removeAdminInvitation: (invitation: EventAdminInvitation) => Promise<void>;
}